import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, Typography
} from "@mui/material";
import {
  RESPONSE_TABS, RESPONSE_ERRORS, formatDateTime
} from "../../common/constants/scheduleModule/responseConstants.js";
import { STRINGS } from "../../common/constants/commonConstants.js";
import { fetchResponseData } from "../../services/scheduleModule/responseServices.js";
import DatePickerComponent from "../../common/components/DatePickerComponent.jsx";

// ── THE NAMING, WRITTEN DOWN ───────────────────────────────────────
//
//   Kept       same-day payments processed through PNM   (CF-AI3-PAY)
//   Promises   future payments SCHEDULED through PNM     (CF-AI4-PTP)
//   PTP        Kept + Promises — everything committed to
//
// ⚠ UNCOMMITTED IS NOT IN PTP AND IS NOT ON THIS PAGE. It is a different
// thing entirely: customers who were given the payment method and never
// selected one. It commits to nothing, so it is not part of PTP and never
// sums into it. `PtpDashboard`'s DPD table is the reference — it carries
// future_ptp_count as its own "Uncommitted" column, separate from the
// promise count that feeds PTP.
//
// Say "Promises" for the scheduled half. Calling it Uncommitted is the easy
// mistake and it makes PTP look like it swallowed something it did not.
//
// ── THE BACKEND COLUMNS DO NOT USE THESE WORDS ──
// `PayCount`/`PayAmount` is Kept; `ProCount`/`ProAmount` is Promises.
//
// So PTP is DERIVED here — Pay + Pro — rather than being the old "Promise"
// column relabelled. Renaming Promise to PTP without adding Pay into it would
// have put PTP directly before Kept as two figures that do not overlap, and a
// reader would take the smaller as a part of the larger.
//
// ⚠ THE PROMISE FIGURE IS NO LONGER SHOWN ON ITS OWN. It is PTP − Kept, and
// no column displays it. Adding "Promises"/"Promises $" back is a COLS entry
// plus a width.
//
// ── THE PALETTE IS THE REPORT'S ──
// rpt1's PDF is the artifact people forward: PTP green, Kept a darker shade of
// that green, Smart Links pink. The R1/R2/… rate ramp is untouched — those are
// a SCALE, not metrics, and they keep their own colouring.
const shade = (hex, pct) => {
  const n = parseInt(String(hex).slice(1), 16);
  const f = (c) => Math.max(0, Math.min(255, Math.round(c + c * pct)));
  return `#${[(n >> 16) & 255, (n >> 8) & 255, n & 255]
    .map(f).map((v) => v.toString(16).padStart(2, "0")).join("")}`;
};
const KEPT_SHADE = -0.42;

const C_PTP  = "#1D9E75";                    // green
const C_KEPT = shade(C_PTP, KEPT_SHADE);     // dark green
const C_SL   = "#D4537E";                    // pink

// Chrome, and the rate ramp below.
const BLUE   = "#378ADD";
const PINK   = "#D4537E";
const AMBER  = "#BA7517";
const GREEN  = "#1D9E75";
const PURPLE = "#7F77DD";

// DPD bucket sort order — matches the backend `dpdSort` CASE in /getresponse.
const DPD_ORDER = {
  "1-9":   1, "10-19": 2, "20-29": 3, "30-39": 4,
  "40-49": 5, "50-59": 6, "60-89": 7, "90+":   8,
  "Other": 9,
};

// Columns that wrap their text instead of being truncated.
const WRAPPING_COLS = new Set(["Message", "CampaignName", "CampaignScheduleName"]);

// One hue per metric for the outcome columns, so a column and the KPI tile
// above it share a colour. Everything not listed falls through to the
// response-rate ramp.
const OUTCOME_COLOR = {
  PtpCount:  C_PTP,
  PtpAmount: C_PTP,
  PayCount:  C_KEPT,
  PayAmount: C_KEPT,
  SLCount:   C_SL,
  SLAmount:  C_SL,
};

// ── Helpers ────────────────────────────────────────────────────────
function responseColor(val) {
  const n = Number(val);
  if (isNaN(n) || n === 0) return "text.disabled";
  if (n >= 75) return GREEN;
  if (n >= 40) return BLUE;
  if (n >= 15) return AMBER;
  return PINK;
}

// Compact dollar display: $1.23K, $4.56M, otherwise $1,234.
function fmtCurrency(n) {
  const num = Number(n);
  if (!num || isNaN(num)) return "$0";
  if (Math.abs(num) >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
  if (Math.abs(num) >= 1_000)     return `$${(num / 1_000).toFixed(2)}K`;
  return `$${num.toFixed(2)}`;
}

// CSV cell escaping — wrap in quotes if it contains comma, quote, or newline.
const csvEscape = (v) => {
  if (v == null) return "";
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// ── Column config ──────────────────────────────────────────────────
// Flags:
//   pct:      true  → format with "%" suffix and color via responseColor
//   currency: true  → format via fmtCurrency
//
// PTP leads its pair and Kept follows: whole, then the part of it that
// arrived. PtpCount/PtpAmount are DERIVED per row — see `rows` below — so the
// display, the sort and the CSV all read the same number.
const COLS = [
  { key: "DPD",                  label: "DPD",         width: 60,  align: "right" },
  { key: "Message",              label: "Message",     width: 240, align: "left"  },
  { key: "Char",                 label: "Char",        width: 55,  align: "right" },
  { key: "CampaignName",         label: "Campaign",    width: 130, align: "left"  },
  { key: "CampaignScheduleName", label: "Schedule",    width: 130, align: "left"  },
  { key: "MsgsSent",             label: "Sent",        width: 60,  align: "right" },
  { key: "R1",                   label: "R1",          width: 50,  align: "right" },
  { key: "R1Pct",                label: "R1 %",        width: 60,  align: "right", pct: true },
  { key: "AvgR",                 label: "Avg R",       width: 65,  align: "right" },
  { key: "PtpCount",             label: "PTP",         width: 60,  align: "right" },
  { key: "PtpAmount",            label: "PTP $",       width: 90,  align: "right", currency: true },
  { key: "PayCount",             label: "Same Day",    width: 75,  align: "right" },
  { key: "PayAmount",            label: "Same Day $",  width: 95,  align: "right", currency: true },
  { key: "SLCount",              label: "SmartLink",   width: 80,  align: "right" },
  { key: "SLAmount",             label: "SmartLink $", width: 95,  align: "right", currency: true },
  { key: "MaxR",                 label: "Max R",       width: 60,  align: "right" },
  { key: "R2",                   label: "R2",          width: 50,  align: "right" },
  { key: "R3",                   label: "R3",          width: 50,  align: "right" },
  { key: "R4",                   label: "R4",          width: 50,  align: "right" },
  { key: "R5",                   label: "R5",          width: 50,  align: "right" },
  { key: "R8",                   label: "R8",          width: 50,  align: "right" },
  { key: "R10",                  label: "R10",         width: 50,  align: "right" },
];

const COL_GAP     = 12;
const GRID_COLS   = COLS.map(c => `${c.width}px`).join(" ");
const TABLE_WIDTH = COLS.reduce((s, c) => s + c.width, 0) + 32 + (COLS.length - 1) * COL_GAP;

// Format a cell value. Null/empty → em-dash; pct → "%"; currency → "$X.XK".
function formatCellValue(col, raw) {
  if (col.currency) return fmtCurrency(raw);
  if (raw == null || raw === "") return "—";
  if (col.pct) {
    const n = Number(raw);
    if (isNaN(n)) return "—";
    return `${n.toFixed(1)}%`;
  }
  return raw;
}

// CSV-friendly cell value. Strips % sign and $ formatting so spreadsheets
// can treat the column numerically. Empty stays empty (not em-dash) so
// cells aggregate correctly in Excel.
function csvCellValue(col, raw) {
  if (raw == null || raw === "") return "";
  if (col.pct || col.currency) {
    const n = Number(raw);
    if (isNaN(n)) return "";
    return col.pct ? n.toFixed(1) : n.toFixed(2);
  }
  return raw;
}

// Color logic per column. Pulled out so the row render stays readable.
function cellColor(col, raw) {
  if (col.key === "DPD")      return AMBER;
  if (col.key === "MsgsSent") return BLUE;
  if (col.key === "Char") {
    // SMS segment threshold — over 160 chars splits into multiple
    // segments and may impact deliverability/cost. Email lengths
    // routinely exceed this, but the cue is harmless there.
    const n = Number(raw) || 0;
    if (n === 0)   return "text.disabled";
    if (n > 160)   return PINK;
    return "text.secondary";
  }
  if (WRAPPING_COLS.has(col.key)) return "text.primary";

  // One hue per metric, dimmed at zero so an empty cell reads as empty rather
  // than as a coloured zero.
  const metric = OUTCOME_COLOR[col.key];
  if (metric) {
    const n = Number(raw) || 0;
    return n > 0 ? metric : "text.disabled";
  }

  return responseColor(raw);
}

// ── Sub-components ─────────────────────────────────────────────────
function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

// ── Main Component ─────────────────────────────────────────────────
const Responses = () => {
  const today        = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [startDate,    setStartDate]    = useState(firstOfMonth);
  const [endDate,      setEndDate]      = useState(today);
  const [responseType, setResponseType] = useState(RESPONSE_TABS.TEXTS);
  const [data,         setData]         = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");
  const [page,         setPage]         = useState(0);
  const [sortKey,      setSortKey]      = useState("R1Pct");
  const [sortDir,      setSortDir]      = useState("desc");
  const rowsPerPage = 25;

  const load = useCallback(async (start, end, type) => {
    setLoading(true);
    setError("");
    setPage(0);
    try {
      const result = await fetchResponseData({
        startDate:    formatDateTime(start),
        endDate:      formatDateTime(end),
        responseType: type,
      });
      setData(result || []);
    } catch (e) {
      console.error(RESPONSE_ERRORS.FETCH_RESPONSE_ERROR, e);
      setError("Failed to load response data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(firstOfMonth, today, RESPONSE_TABS.TEXTS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(0);
  };

  // PTP is Kept + Promises (NOT Uncommitted — see the header), derived once
  // per row so the table, the sort and the CSV all read the same number — and the same number the KPI tile shows.
  // Deriving it at render time instead would leave the sort comparing a field
  // that does not exist on the row.
  const rows = useMemo(() => data.map(r => {
    const kept    = Number(r.PayCount)  || 0;
    const promise = Number(r.ProCount)  || 0;
    const keptAmt = Number(r.PayAmount) || 0;
    const proAmt  = Number(r.ProAmount) || 0;
    return {
      ...r,
      PtpCount:  kept + promise,
      PtpAmount: keptAmt + proAmt,
    };
  }), [data]);

  // Sort always runs DPD asc as the primary key, then the user-selected
  // column as secondary — keeps related DPD buckets grouped together
  // even when sorting by a metric (matches backend ORDER BY).
  const sortedData = useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const dpdCmp = (DPD_ORDER[a.DPD] ?? 99) - (DPD_ORDER[b.DPD] ?? 99);
      if (dpdCmp !== 0) return dpdCmp;

      if (sortKey === "DPD") {
        return sortDir === "asc" ? 0 : 0;
      }

      const av = a[sortKey];
      const bv = b[sortKey];
      const an = Number(av);
      const bn = Number(bv);
      const cmp = !isNaN(an) && !isNaN(bn)
        ? an - bn
        : String(av ?? "").localeCompare(String(bv ?? ""));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [rows, sortKey, sortDir]);

  const pagedData  = useMemo(() =>
    sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  , [sortedData, page]);

  const totalPages = Math.ceil(data.length / rowsPerPage);

  const kpis = useMemo(() => {
    const totalSent = data.reduce((a,b) => a + (Number(b.MsgsSent)  || 0), 0);
    const totalR1   = data.reduce((a,b) => a + (Number(b.R1)        || 0), 0);
    const keptAmt   = data.reduce((a,b) => a + (Number(b.PayAmount) || 0), 0);
    const proAmt    = data.reduce((a,b) => a + (Number(b.ProAmount) || 0), 0);
    const slAmt     = data.reduce((a,b) => a + (Number(b.SLAmount)  || 0), 0);
    const avgR      = data.length
      ? data.reduce((a,b) => a + (Number(b.AvgR) || 0), 0) / data.length
      : 0;
    const maxR = Math.max(0, ...data.map(r => Number(r.MaxR) || 0));

    // PTP is the two halves together; Kept is the payment half on its own.
    const ptpAmt = keptAmt + proAmt;

    return {
      totalSent, totalR1, avgR, maxR,
      records:  data.length,
      ptpAmt, keptAmt, slAmt,
      // Unchanged in value, but note what it now is: a total plus a sibling.
      // Smart links are their own event code, not a part of PTP.
      totalAmt: ptpAmt + slAmt,
      keptPct:  ptpAmt ? (keptAmt / ptpAmt) * 100 : 0,
    };
  }, [data]);

  // ── CSV export ─────────────────────────────────────────────────
  const exportDisabled = loading || sortedData.length === 0;

  const exportCsv = () => {
    if (!sortedData.length) return;

    const headers = COLS.map(c => csvEscape(c.label));
    const rows = sortedData.map(row =>
      COLS.map(col => csvEscape(csvCellValue(col, row[col.key]))).join(",")
    );
    const csv  = "\uFEFF" + [headers.join(","), ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);

    const now = new Date();
    const ts  = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,"0")}${String(now.getDate()).padStart(2,"0")}`;
    const channel = String(responseType).toLowerCase();

    const a = document.createElement("a");
    a.href     = url;
    a.download = `responses_${channel}_${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const gridSx = {
    display: "grid",
    gridTemplateColumns: GRID_COLS,
    columnGap: `${COL_GAP}px`,
    width: `${TABLE_WIDTH}px`,
    px: 2,
  };

  return (
    <Box sx={{ p: 0 }}>

      {/* ── Filters ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2,
        alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <DatePickerComponent
          label={STRINGS.START_DATE}
          date={startDate}
          onDateChange={setStartDate}
          marginRight={0}
        />
        <DatePickerComponent
          label={STRINGS.END_DATE}
          date={endDate}
          onDateChange={d => { if (d >= startDate) setEndDate(d); }}
          marginRight={0}
        />
        <Box
          onClick={() => load(startDate, endDate, responseType)}
          sx={{
            px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
            cursor: "pointer", userSelect: "none",
            background: BLUE, color: "#fff", fontWeight: 500,
            alignSelf: "flex-end", mb: "1px",
            "&:hover": { background: "#2a6db5" },
          }}
        >
          Search
        </Box>

        <Box
          onClick={exportDisabled ? undefined : exportCsv}
          title={exportDisabled ? "No data to export" : "Export current view to CSV"}
          sx={{
            px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
            userSelect: "none",
            border: "0.5px solid",
            borderColor: exportDisabled ? "divider" : BLUE,
            background: "transparent",
            color: exportDisabled ? "text.disabled" : BLUE,
            fontWeight: 500,
            cursor: exportDisabled ? "default" : "pointer",
            opacity: exportDisabled ? 0.5 : 1,
            alignSelf: "flex-end", mb: "1px",
            display: "inline-flex", alignItems: "center", gap: "6px",
            transition: "all .15s",
            "&:hover": exportDisabled ? {} : { background: "rgba(55,138,221,0.06)" },
          }}
        >
          <span style={{ fontSize: 14, lineHeight: 1 }}>↓</span>
          Export CSV
        </Box>

        <Box sx={{
          display: "flex", ml: "auto",
          border: "0.5px solid", borderColor: "divider",
          borderRadius: "20px", overflow: "hidden",
          alignSelf: "flex-end",
        }}>
          {[RESPONSE_TABS.TEXTS, RESPONSE_TABS.EMAILS].map(ch => (
            <Box key={ch} onClick={() => {
              setResponseType(ch);
              load(startDate, endDate, ch);
            }} sx={{
              px: "14px", py: "5px", fontSize: 13,
              fontWeight:  responseType === ch ? 500 : 400,
              cursor: "pointer", userSelect: "none",
              background:  responseType === ch ? BLUE : "transparent",
              color:       responseType === ch ? "#fff" : "text.secondary",
              transition:  "all .15s",
            }}>
              {ch}
            </Box>
          ))}
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
          <CircularProgress size={20} /> Loading…
        </Box>
      )}

      {!loading && data.length === 0 && !error && (
        <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
          No data for selected range.
        </Typography>
      )}

      {!loading && data.length > 0 && (
        <>
          {/* ── KPIs — first row: response counts ── */}
          <SectionTitle>Summary</SectionTitle>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
            {[
              { label: "Records",    value: kpis.records.toLocaleString(),   color: BLUE   },
              { label: "Total sent", value: kpis.totalSent.toLocaleString(), color: BLUE   },
              { label: "Total R1",   value: kpis.totalR1.toLocaleString(),   color: GREEN  },
              { label: "Avg R",      value: kpis.avgR.toFixed(1),            color: AMBER  },
              { label: "Max R",      value: kpis.maxR.toLocaleString(),      color: PURPLE },
            ].map(k => (
              <Box key={k.label} sx={{
                background: "#f7f7f7", borderRadius: 2,
                p: "10px 14px", flex: 1, minWidth: 0,
              }}>
                <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
                  {k.label}
                </Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 500, color: k.color, lineHeight: 1.2 }}>
                  {k.value}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* ── KPIs — second row: outcome amounts ──
              PTP leads and Kept follows: whole, then the part of it that
              arrived. Smart links sit apart from that pair — a smart link is
              its own event code, not a component of PTP. */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            {[
              { label: "PTP $",       value: fmtCurrency(kpis.ptpAmt),   color: C_PTP  },
              { label: "Same Day $",  value: fmtCurrency(kpis.keptAmt),  color: C_KEPT },
              { label: "SmartLink $", value: fmtCurrency(kpis.slAmt),    color: C_SL   },
              { label: "Total $",     value: fmtCurrency(kpis.totalAmt), color: AMBER  },
            ].map(k => (
              <Box key={k.label} sx={{
                background: "#f7f7f7", borderRadius: 2,
                p: "10px 14px", flex: 1, minWidth: 0,
              }}>
                <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
                  {k.label}
                </Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 500, color: k.color, lineHeight: 1.2 }}>
                  {k.value}
                </Typography>
              </Box>
            ))}
          </Box>

          <Typography sx={{
            fontSize: 11, color: "text.disabled", mb: 2, lineHeight: 1.5,
          }}>
            <b>PTP</b> is Same Day + Promises — {fmtCurrency(kpis.keptAmt)} of{" "}
            {fmtCurrency(kpis.ptpAmt)} arrived, {kpis.keptPct.toFixed(1)}%. Promises are
            future payments scheduled through PNM; the figure is not shown on its own
            here, it is PTP minus Same Day. <b>Total $</b> is PTP plus smart links, which are
            their own event code rather than a part of PTP.
          </Typography>

          {/* ── Table ── */}
          <SectionTitle>Detail</SectionTitle>

          <Box sx={{
            border: "0.5px solid", borderColor: "divider",
            borderRadius: 2, overflowX: "auto",
          }}>
            {/* Header */}
            <Box sx={{
              ...gridSx,
              py: 1,
              background: "#f7f7f7",
              borderBottom: "0.5px solid", borderColor: "divider",
              fontSize: 11, fontWeight: 500,
              textTransform: "uppercase", letterSpacing: "0.05em",
            }}>
              {COLS.map(col => (
                <Box
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  sx={{
                    textAlign: col.align,
                    cursor: "pointer", userSelect: "none",
                    display: "flex", gap: 0.5,
                    alignItems: "center",
                    justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                    color: sortKey === col.key ? BLUE : "text.secondary",
                    "&:hover": { color: BLUE },
                    ...(WRAPPING_COLS.has(col.key) && { minWidth: 0 }),
                  }}
                >
                  {col.label}
                  {sortKey === col.key && (
                    <span style={{ fontSize: 10, flexShrink: 0 }}>
                      {sortDir === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </Box>
              ))}
            </Box>

            {/* Rows */}
            {pagedData.map((row, i) => (
              <Box key={row.Id ?? i} sx={{
                ...gridSx,
                py: "7px",
                fontSize: 12,
                borderBottom: "0.5px solid", borderColor: "divider",
                background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                "&:hover": { background: "rgba(55,138,221,0.06)" },
              }}>
                {COLS.map(col => {
                  const raw    = row[col.key];
                  const numVal = Number(raw) || 0;
                  const isText = WRAPPING_COLS.has(col.key);
                  return (
                    <Box key={col.key} sx={{
                      textAlign: col.align,
                      color: cellColor(col, raw),
                      fontWeight:
                        col.key === "DPD" || col.key === "MsgsSent"
                          ? 500
                          : numVal > 0 ? 500 : 400,
                      ...(isText && {
                        whiteSpace: "normal",
                        wordBreak:  "break-word",
                        minWidth:   0,
                      }),
                    }}>
                      {formatCellValue(col, raw)}
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <Box sx={{
              display: "flex", justifyContent: "space-between",
              alignItems: "center", mt: 1, fontSize: 12,
              color: "text.secondary", px: 1,
            }}>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                Showing {page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, data.length)} of {data.length}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Box
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  sx={{
                    px: "10px", py: "3px", borderRadius: "20px",
                    border: "0.5px solid", borderColor: "divider",
                    cursor: page === 0 ? "default" : "pointer",
                    opacity: page === 0 ? 0.4 : 1,
                    "&:hover": { borderColor: page === 0 ? "divider" : BLUE },
                  }}
                >
                  ‹
                </Box>
                <span>Page {page + 1} of {totalPages}</span>
                <Box
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  sx={{
                    px: "10px", py: "3px", borderRadius: "20px",
                    border: "0.5px solid", borderColor: "divider",
                    cursor: page >= totalPages - 1 ? "default" : "pointer",
                    opacity: page >= totalPages - 1 ? 0.4 : 1,
                    "&:hover": { borderColor: page >= totalPages - 1 ? "divider" : BLUE },
                  }}
                >
                  ›
                </Box>
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default Responses;